import { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, ArrowRight, BadgeCheck, RefreshCw, Upload, FileText, X, Mail, Loader2 } from 'lucide-react';
import { API_BASE_URL, apiService } from '@/shared/services/api';
import { EnrollmentApi } from '@/features/enrollment';

type Props = {
  enrollmentData: Record<string, any>;
  updateEnrollmentData?: (field: string, value: any) => void;
  handleNavigate: (targetIndex: number) => Promise<void>;
  handleSimpleNavigate?: (targetIndex: number) => void;
  handleNavigateWithCompletion?: (targetIndex: number, steps?: any[]) => Promise<void>;
  sessionId?: string | null;
  isPublicEnrollment?: boolean;
  isNavigating?: boolean;
  isCompleting?: boolean;
  currentStepIndex?: number;
};

export default function Step7IdentityPreview({ enrollmentData, updateEnrollmentData, handleNavigate, handleSimpleNavigate, handleNavigateWithCompletion, sessionId, isPublicEnrollment = false, isNavigating, isCompleting, currentStepIndex }: Props) {
  
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderSent, setReminderSent] = useState(false);
  const [isContinuing, setIsContinuing] = useState(false);

  const [driverLicenseError, setDriverLicenseError] = useState<string | null>(null);
  const [uploadingDriverLicense, setUploadingDriverLicense] = useState(false);
  const [driverLicenseViewUrl, setDriverLicenseViewUrl] = useState<string | null>(null);
  const [driverLicenseViewLoading, setDriverLicenseViewLoading] = useState(false);

  const idUrl = enrollmentData.idDocumentUrl;
  const apiOrigin = API_BASE_URL.replace(/\/api$/, '');
  const fileUrl = signedUrl || documentUrl || (idUrl && (idUrl.startsWith('http') ? idUrl : `${apiOrigin}${idUrl}`));

  const driverLicenseDataRaw = Array.isArray(enrollmentData?.driversLicense)
    ? enrollmentData?.driversLicense?.[0]
    : enrollmentData?.driversLicense;

  const isRobloxCampaign =
    enrollmentData?.campaignId === 'roblox-exploitation' ||
    enrollmentData?.selectedCampaign === 'robloxExploitation' ||
    enrollmentData?.campaign?.name?.toLowerCase().includes('roblox');

  const handleDriverLicenseFileSelect = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) {
      return;
    }

    const file = fileList[0];
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];

    if (!allowedTypes.includes(file.type)) {
      setDriverLicenseError('File type not supported. Please upload JPEG, PNG, GIF, or PDF files.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setDriverLicenseError('File size must be less than 10 MB');
      return;
    }

    setDriverLicenseError(null);

    uploadDriverLicense(file)
      .catch((err) => {
        console.error('Driver\'s license upload failed:', err);
        setDriverLicenseError(err?.message || 'Failed to upload driver\'s license. Please try again.');
      });
  };

  const uploadDriverLicense = async (driverLicenseFile: File) => {
    if (!sessionId) {
      setDriverLicenseError('Session ID is missing. Please refresh the page and try again.');
      return;
    }

    setUploadingDriverLicense(true);
    setDriverLicenseError(null);

    try {
      const formData = new FormData();
      formData.append('document', driverLicenseFile);
      formData.append('documentType', 'driversLicense');

      const response = await apiService.post(`/enrollments/sessions/${sessionId}/roblox/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const fileData = response.data?.data || response.data;

      if (updateEnrollmentData) {
        updateEnrollmentData('driversLicense', {
          url: fileData?.documentUrl || fileData?.url,
          fileName: fileData?.fileName || driverLicenseFile.name,
          fileSize: fileData?.fileSize || driverLicenseFile.size,
          uploadedAt: fileData?.uploadedAt || new Date().toISOString(),
          mimeType: driverLicenseFile.type,
          name: driverLicenseFile.name,
          size: driverLicenseFile.size,
          lastModified: driverLicenseFile.lastModified,
          isPending: false,
        });
      }

    } catch (err: any) {
      console.error('Driver\'s license upload failed:', err);
      throw err;
    } finally {
      setUploadingDriverLicense(false);
    }
  };

  // Fetch driver's license from session if it exists (e.g., uploaded via email link)
  const getDriverLicenseFromSession = useCallback(async () => {
    if (!sessionId) {
      console.log('❌ No sessionId, cannot fetch driver\'s license');
      return null;
    }

    try {
      console.log('📡 Fetching session data for sessionId:', sessionId);
      const session = await EnrollmentApi.getSession(sessionId);
      console.log('📦 Full session object:', JSON.stringify(session, null, 2));

      if (session?.data) {
        const sessionData = session.data as any;

        // Check multiple possible locations for driver's license
        console.log('🔍 Checking all possible locations...');

        // Check if idDocumentUrl exists (driver's license stored at root level)
        const idDocumentUrl = (session as any).idDocumentUrl;
        const idDocumentType = (session as any).idDocumentType;
        const idDocumentUploadedAt = (session as any).idDocumentUploadedAt;

        console.log('📍 Root level - idDocumentUrl:', idDocumentUrl);
        console.log('📍 Root level - idDocumentType:', idDocumentType);

        // Check ALL steps for driver's license (in case it's stored in step data)
        console.log('📍 step5.driversLicense:', sessionData.step5?.driversLicense);
        console.log('📍 session.data.driversLicense:', sessionData.driversLicense);

        // Try to find driver's license - prioritize root level idDocumentUrl
        let driversLicense = null;

        if (idDocumentUrl) {
          // Found driver's license at root level (uploaded via email link)
          driversLicense = {
            url: idDocumentUrl,
            mimeType: idDocumentType || 'application/octet-stream',
            uploadedAt: idDocumentUploadedAt || new Date().toISOString(),
            fileName: 'Driver License',
            source: 'email_upload'
          };
        } else {
          // Fall back to checking step data
          driversLicense = sessionData.step5?.driversLicense ||
                          sessionData.driversLicense;
        }

        console.log('🪪 Driver\'s license found:', driversLicense);

        if (driversLicense && updateEnrollmentData) {
          console.log('✅ Found driver\'s license in session, populating to enrollmentData');
          // Populate into enrollmentData so it displays in the form
          updateEnrollmentData('driversLicense', driversLicense);
          return driversLicense;
        } else {
          console.log('⚠️ No driver\'s license found in any location');
          console.log('Full session.data keys:', Object.keys(sessionData));
        }
      } else {
        console.log('⚠️ No session.data found');
      }
      return null;
    } catch (error) {
      console.error('❌ Failed to fetch driver\'s license from session:', error);
      return null;
    }
  }, [sessionId, updateEnrollmentData]);

  const refreshDriverLicenseViewUrl = useCallback(async () => {
    if (!driverLicenseDataRaw?.url) {
      setDriverLicenseViewUrl(null);
      return;
    }

    if (!sessionId) {
      setDriverLicenseError('Session ID is missing. Please refresh and try again.');
      setDriverLicenseViewUrl(null);
      return;
    }

    if (isPublicEnrollment) {
      if (driverLicenseDataRaw.url.startsWith('http')) {
        setDriverLicenseViewUrl(driverLicenseDataRaw.url);
      } else {
        setDriverLicenseViewUrl(`${apiOrigin}${driverLicenseDataRaw.url}`);
      }
      setDriverLicenseError(null);
      return;
    }

    setDriverLicenseViewLoading(true);
    setDriverLicenseError(null);

    try {
      const response = await EnrollmentApi.getSignedDocumentUrl(sessionId, 'driversLicense');
      setDriverLicenseViewUrl(response.signedUrl);

      if (updateEnrollmentData && driverLicenseDataRaw) {
        updateEnrollmentData('driversLicense', {
          ...driverLicenseDataRaw,
          viewUrl: response.signedUrl,
          signedUrlFetchedAt: new Date().toISOString()
        });
      }
    } catch (error: any) {
      console.error('Failed to fetch driver\'s license signed URL:', error);
      setDriverLicenseError(error?.message || 'Failed to generate secure link. Please try again.');
      setDriverLicenseViewUrl(null);
    } finally {
      setDriverLicenseViewLoading(false);
    }
  }, [apiOrigin, driverLicenseDataRaw?.url, isPublicEnrollment, sessionId, updateEnrollmentData]);

  const handleBack = () => {
    // Calculate previous step dynamically
    const prevStepIndex = (currentStepIndex ?? 6) - 1
    
    if (handleSimpleNavigate) {
      handleSimpleNavigate(prevStepIndex)
    } else if (handleNavigate) {
      handleNavigate(prevStepIndex)
    }
  }

  // Simple function to get document URL directly from session
  const getDocumentFromSession = async (): Promise<string | null> => {
    if (!sessionId) return null;
    
    try {
      const session = await EnrollmentApi.getSession(sessionId);
    
      if (session.data) {
        // Check multiple locations for document URL
        let docUrl = '';
        if (session.data.idDocumentUrl) {
          docUrl = session.data.idDocumentUrl;
        } else if (session.data.data && session.data.data.idDocumentUrl) {
          docUrl = session.data.data.idDocumentUrl;
        } else if (session.data.data && session.data.data.step2 && session.data.data.step2.identity && session.data.data.step2.identity.url) {
          docUrl = session.data.data.step2.identity.url;
        }
        
        if (docUrl) {
          setDocumentUrl(docUrl);
          setError(null);
          return docUrl;
        } else {
          setError('No identity document received yet. Please upload a document above.');
          return null;
        }
      }
      return null;
    } catch (err) {
      console.error('Error fetching session:', err);
      setError('Failed to load document from session');
      return null;
    }
  };

  // Fetch document from session when component mounts
  useEffect(() => {
    if (sessionId) {
      // Always try to get document from session for public enrollment
      if (isPublicEnrollment) {
        getDocumentFromSession();
      } else if (idUrl) {
        // For agent enrollment, fetch signed URL if idUrl exists
        fetchSignedUrl();
      } else {
        // For agent enrollment, also try to get from session if no idUrl in enrollmentData
        getDocumentFromSession();
      }
    }
  }, [sessionId, isPublicEnrollment, idUrl]);

  // Additional effect to refresh document data when component mounts
  useEffect(() => {
    if (sessionId) {
      // Always refresh document data from session when component mounts
      getDocumentFromSession();
    }
  }, [sessionId]); // Only depend on sessionId to avoid infinite loops

  // Fetch signed URL when documentUrl or idUrl changes (for agent enrollment)
  useEffect(() => {
    if (sessionId && !isPublicEnrollment && (documentUrl || idUrl)) {
      fetchSignedUrl();
    }
  }, [documentUrl, idUrl, sessionId, isPublicEnrollment]);

  // Load driver's license from session on mount (if uploaded via email link)
  useEffect(() => {
    if (isRobloxCampaign && sessionId && !driverLicenseDataRaw?.url) {
      console.log('🔍 Checking session for existing driver\'s license upload...');
      console.log('SessionId:', sessionId);
      console.log('Current driverLicenseDataRaw:', driverLicenseDataRaw);
      getDriverLicenseFromSession();
    }
  }, [isRobloxCampaign, sessionId, driverLicenseDataRaw?.url, getDriverLicenseFromSession]);

  useEffect(() => {
    if (isRobloxCampaign && driverLicenseDataRaw?.url) {
      refreshDriverLicenseViewUrl();
    } else {
      setDriverLicenseViewUrl(null);
    }
  }, [isRobloxCampaign, driverLicenseDataRaw?.url, refreshDriverLicenseViewUrl]);

  const fetchSignedUrl = async (urlToUse?: string | null) => {
    if (!sessionId) {
      setError('Missing session ID. Please refresh the page.');
      return;
    }
    
    // Use provided URL or fall back to state
    const documentUrlToUse = urlToUse || documentUrl || idUrl;
    
    if (!documentUrlToUse) {
      setError('No identity document uploaded yet. Please upload a document above.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      if (isPublicEnrollment) {
        // For public enrollment, just use the URL directly if it's a full URL
        if (documentUrlToUse && documentUrlToUse.startsWith('http')) {
          setSignedUrl(documentUrlToUse);
        } else if (documentUrlToUse) {
          // Try to construct the URL
          const apiOrigin = API_BASE_URL.replace(/\/api$/, '');
          setSignedUrl(`${apiOrigin}${documentUrlToUse}`);
        }
      } else {
        // For agent enrollment, use the generic signed URL endpoint with documentType='idDocument'
        const result = await EnrollmentApi.getSignedDocumentUrl(sessionId, 'idDocument');
        setSignedUrl(result.signedUrl);
      }
    } catch (err) {
      console.error('Failed to get signed URL:', err);
      setError('Failed to load document. Please try refreshing.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setUploadError('Please select a valid file type (JPEG, PNG, GIF, or PDF)');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setUploadError('File size must be less than 10MB');
        return;
      }
      
      setUploadedFile(file);
      setUploadError(null);
    }
  };

  const handleFileUpload = async () => {
    if (!uploadedFile || !sessionId) {
      setUploadError('Please select a file to upload');
      return;
    }
  
    setUploading(true);
    setUploadError(null);
    
    try {
      const formData = new FormData();
      formData.append('idDocument', uploadedFile);
      formData.append('consentAgreed', 'true');
      
      const response = await fetch(`${API_BASE_URL}/enrollments/public/sessions/${sessionId}/identity`, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      const result = await response.json();
      setUploadedFile(null);
      
      if (result.success && result.data) {
        // ⭐ NEW: Update enrollment data with the new document URL
        if (updateEnrollmentData && result.data.idDocumentUrl) {
          updateEnrollmentData('idDocumentUrl', result.data.idDocumentUrl);
          updateEnrollmentData('identityConsentAgreed', true);
        }
        
        // Show success message
        alert('Document uploaded successfully!');
        
        // ⭐ NEW: Update the document display without reloading
        setDocumentUrl(result.data.idDocumentUrl);
        setSignedUrl(null); // Clear current signed URL to force re-fetch
        setError(null);
        
        // Refresh the document display
        await getDocumentFromSession();
      }
      
    } catch (err) {
      console.error('Upload failed:', err);
      if (err instanceof Error) {
        setUploadError(err.message);
      } else {
        setUploadError('Failed to upload document. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const removeSelectedFile = () => {
    setUploadedFile(null);
    setUploadError(null);
  };

  const handleSendReminder = async () => {
    if (!sessionId) {
      alert('Session ID is missing. Please refresh the page.');
      return;
    }

    // Get the email to send to (prefer injured party email, fallback to client email)
    const emailToSend = enrollmentData.injuredPartyEmail || enrollmentData.clientEmail;
    if (!emailToSend) {
      alert('No email address found. Please ensure the client email is filled in Step 2.');
      return;
    }

    setSendingReminder(true);
    try {
      await EnrollmentApi.sendIdReminderEmail(sessionId, emailToSend);
      setReminderSent(true);
      alert(`ID reminder email sent successfully to ${emailToSend}!`);
    } catch (error) {
      console.error('Failed to send reminder email:', error);
      alert('Failed to send reminder email. Please try again.');
    } finally {
      setSendingReminder(false);
    }
  };

  if (isRobloxCampaign) {
    return (
      <div className="space-y-6">
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <BadgeCheck className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-accent-900">Driver&apos;s License Upload</h3>
              <p className="text-sm text-accent-600">
                Upload and review the guardian&apos;s Driver&apos;s License before proceeding.
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-accent-700 mb-2">
                Upload Driver&apos;s License <span className="text-red-500">*</span>
              </label>

              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  driverLicenseError
                    ? 'border-red-500 bg-red-50'
                    : driverLicenseDataRaw?.url
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,.pdf"
                  className="hidden"
                  id="drivers-license-upload-step7"
                  onChange={(e) => handleDriverLicenseFileSelect(e.target.files)}
                />
                <label
                  htmlFor="drivers-license-upload-step7"
                  className="cursor-pointer flex flex-col items-center space-y-2"
                >
                  <Upload className="w-8 h-8 text-accent-500" />
                  <div>
                    <p className="text-sm font-medium text-accent-900">Click to upload Driver&apos;s License</p>
                    <p className="text-xs text-accent-600">JPEG, PNG, GIF, or PDF (max 10MB)</p>
                  </div>
                </label>

                {uploadingDriverLicense && (
                  <p className="mt-3 text-sm text-accent-600 flex items-center space-x-2 justify-center">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Uploading Driver&apos;s License...</span>
                  </p>
                )}
                {driverLicenseError && (
                  <p className="mt-3 text-sm text-red-600">{driverLicenseError}</p>
                )}
              </div>

            </div>

            {driverLicenseDataRaw?.url && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
                <p className="font-medium">Driver&apos;s License uploaded successfully.</p>
                <p className="mt-1 text-xs text-green-700">
                  {driverLicenseDataRaw.fileName || driverLicenseDataRaw.name || 'drivers_license'}
                </p>
                <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
                  {driverLicenseViewLoading && (
                    <div className="inline-flex items-center space-x-2 text-green-700">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Preparing secure link…</span>
                    </div>
                  )}

                  {!driverLicenseViewLoading && driverLicenseViewUrl && (
                    <a
                      href={driverLicenseViewUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center space-x-2 text-green-700 underline"
                    >
                      <Upload className="w-4 h-4" />
                      <span>View Uploaded File</span>
                    </a>
                  )}

                  {!driverLicenseViewLoading && !driverLicenseViewUrl && (
                    <span className="text-xs text-green-700">
                      Secure view link unavailable. Please ensure the driver's license is uploaded.
                    </span>
                  )}
                </div>
                {driverLicenseError && (
                  <p className="mt-2 text-xs text-red-600">{driverLicenseError}</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-between">
          <button
            className="btn-outline flex items-center space-x-2 min-w-[100px] justify-center"
            onClick={handleBack}
            disabled={isNavigating}
          >
            <>
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </>
          </button>

          <button
            className="btn-primary flex items-center space-x-2 min-w-[120px] justify-center"
            onClick={async () => {
              setIsContinuing(true);

              const existingLicense = Array.isArray(enrollmentData?.driversLicense)
                ? enrollmentData?.driversLicense?.[0]
                : enrollmentData?.driversLicense;

              if (uploadingDriverLicense) {
                setDriverLicenseError('Please wait for the driver\'s license upload to complete.');
                setIsContinuing(false);
                return;
              }

              if (!existingLicense?.url) {
                setDriverLicenseError('Driver\'s license upload is required before continuing.');
                setIsContinuing(false);
                return;
              }

              const nextStepIndex = (currentStepIndex ?? 6) + 1;

              try {
                if (handleNavigateWithCompletion) {
                  await handleNavigateWithCompletion(nextStepIndex);
                } else {
                  await handleNavigate(nextStepIndex);
                }
              } finally {
                setIsContinuing(false);
              }
            }}
            disabled={isNavigating || isContinuing}
          >
            {isNavigating || isContinuing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <span>Continue</span>
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
            <BadgeCheck className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-accent-900">Identity Document (Optional)</h3>
            <p className="text-sm text-accent-600">Preview the uploaded ID or send a reminder to the client to upload their ID</p>
          </div>
        </div>

        {/* Simple Upload Section */}
        <div className="mb-6">
          <h4 className="text-lg font-medium text-accent-900 mb-4">Upload Identity Document (Optional)</h4>
          
          <div className="bg-accent-50 border border-accent-200 rounded-lg p-4">
            <div className="space-y-4">
              <div className="border-2 border-dashed border-accent-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,.pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="identity-document-upload"
                />
                <label
                  htmlFor="identity-document-upload"
                  className="cursor-pointer flex flex-col items-center space-y-2"
                >
                  <Upload className="w-8 h-8 text-accent-500" />
                  <div>
                    <p className="text-sm font-medium text-accent-900">
                      Click to upload your ID document
                    </p>
                    <p className="text-xs text-accent-600">
                      JPEG, PNG, GIF, or PDF (max 10MB)
                    </p>
                  </div>
                </label>
              </div>

              {uploadedFile && (
                <div className="bg-white border border-accent-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <FileText className="w-4 h-4 text-accent-600" />
                      <span className="text-sm text-accent-900">{uploadedFile.name}</span>
                    </div>
                    <button
                      onClick={removeSelectedFile}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {uploadError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{uploadError}</p>
                </div>
              )}

              {uploadedFile && (
                <button
                  onClick={handleFileUpload}
                  disabled={uploading}
                  className="w-full btn-primary flex items-center justify-center space-x-2"
                >
                  {uploading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  <span>{uploading ? 'Uploading...' : 'Upload Document'}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Send Reminder Section */}
        <div className="mb-6">
          <h4 className="text-lg font-medium text-accent-900 mb-4">Send ID Upload Reminder</h4>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm text-blue-800 mb-2">
                  <strong>Client Email:</strong> {enrollmentData.injuredPartyEmail || enrollmentData.clientEmail || 'No email found'}
                </p>
                <p className="text-xs text-blue-600">
                  Send a reminder email to the client to upload their ID document. This will send them a professional email with instructions.
                </p>
              </div>
              <div className="ml-4">
                <button
                  onClick={handleSendReminder}
                  disabled={sendingReminder || reminderSent || !(enrollmentData.injuredPartyEmail || enrollmentData.clientEmail)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium ${
                    sendingReminder || reminderSent || !(enrollmentData.injuredPartyEmail || enrollmentData.clientEmail)
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {sendingReminder ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4" />
                  )}
                  <span>
                    {sendingReminder ? 'Sending...' : reminderSent ? 'Reminder Sent!' : 'Send Reminder'}
                  </span>
                </button>
              </div>
            </div>
            {reminderSent && (
              <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-800">
                ✅ Reminder email sent successfully! The client will receive instructions to upload their ID.
              </div>
            )}
          </div>
        </div>

        {/* Document Display */}
        {fileUrl ? (
          <div className="bg-accent-50 border border-accent-200 rounded-lg p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
              <div className="border border-accent-200 rounded-lg overflow-hidden bg-white">
                {loading ? (
                  <div className="p-8 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-accent-500" />
                    <p className="text-sm text-accent-600">Loading document...</p>
                  </div>
                ) : (
                  <>
                    {/* Display PDF in iframe or image */}
                    {fileUrl.match(/\.pdf$/i) ? (
                      <div className="p-4">
                        <iframe
                          src={fileUrl}
                          title="ID Document Preview"
                          className="w-full h-96 border-none"
                          style={{ minHeight: '400px' }}
                        />
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary-700 underline text-sm mt-2 inline-block"
                        >
                          Download PDF
                        </a>
                      </div>
                    ) : (
                      <img
                        src={fileUrl}
                        alt="ID Document"
                        className="w-full max-h-96 object-contain"
                        onError={() => setError('Failed to load image. Please try refreshing.')}
                      />
                    )}
                  </>
                )}
              </div>
              <div className="text-sm text-accent-700">
                <p>
                  <span className="font-semibold">Consent:</span>{' '}
                  {enrollmentData.identityConsentAgreed ? 'Agreed' : 'Not captured'}
                </p>
                <p className="mt-2">
                  If the ID is incorrect, you can upload a new document above or go back to Stage 2 and resend the identity form link.
                </p>
                <button
                  onClick={async () => {
                    setSignedUrl(null);
                    setLoading(true);
                    setError(null);
                    try {
                      // Get document from session first and get the URL directly
                      const docUrl = await getDocumentFromSession();
                      // Then fetch signed URL if we're in agent enrollment mode
                      // Pass the URL directly to avoid timing issues with state updates
                      if (!isPublicEnrollment && sessionId && docUrl) {
                        await fetchSignedUrl(docUrl);
                      } else if (isPublicEnrollment && docUrl) {
                        // For public enrollment, just set the URL directly
                        setSignedUrl(docUrl);
                        setLoading(false);
                      } else {
                        setLoading(false);
                      }
                    } catch (err) {
                      console.error('Failed to refresh document:', err);
                      setError('Failed to refresh document. Please try again.');
                      setLoading(false);
                    }
                  }}
                  className="btn-outline text-sm mt-4 flex items-center space-x-2"
                  disabled={loading}
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Refresh Document</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-orange-800 text-sm">
            {error || 'No identity document uploaded yet. You can upload a document above or send a reminder to the client.'}
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <button
          className="btn-outline flex items-center space-x-2 min-w-[100px] justify-center"
          onClick={handleBack}
          disabled={isNavigating}
        >
          <>
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </>
        </button>

        <button
          className="btn-primary flex items-center space-x-2 min-w-[120px] justify-center"
          onClick={async () => {
            setIsContinuing(true);
            const nextStepIndex = (currentStepIndex ?? 6) + 1
            
            try {
              // Use handleNavigateWithCompletion to properly save step data and navigate
              if (handleNavigateWithCompletion) {
                await handleNavigateWithCompletion(nextStepIndex)
              } else {
                await handleNavigate(nextStepIndex)
              }
            } finally {
              setIsContinuing(false);
            }
          }}
          disabled={isNavigating || isContinuing}
        >
          {isNavigating || isContinuing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            <>
              <span>Continue</span>
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}